import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { submitClaimFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  isSubmitClaimStepValid,
  SubmitClaimState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { StoreCreditType } from "@redotech/redo-return-core-common/return-flow/return-option-enums";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import RefreshIcon from "@redotech/redo-web/icon-old/refresh.svg";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";
import { getModelReturnOptionMethod } from "./return";

export function returnOptionLabels(
  value: ReturnOptionMethod["type"] | undefined,
): string | undefined {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (value) {
    case ReturnOptionMethod.STORE_CREDIT:
      return t("Store credit");
    case ReturnOptionMethod.EXCHANGE:
      return t("Same Item Exchange");
    case ReturnOptionMethod.REFUND:
      return t("Refund");
  }
  return undefined;
}

export const SUBMIT_CLAIM = (
  Details: React.ComponentType<StepTypeDetailsProps<SubmitClaimState>>,
): StepType<SubmitClaimState, ModelStep.SubmitClaim> => ({
  Details: (props: StepTypeDetailsProps<SubmitClaimState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.SUBMIT_CLAIM],
  valid(state) {
    return isSubmitClaimStepValid(state).valid;
  },
  Icon: RefreshIcon,
  iconColor: "success",
  description(state) {
    return returnOptionLabels(state.option.method) || "";
  },
  downstream() {
    return [];
  },
  maxDownstream() {
    return 0;
  },
  empty: {
    option: {
      method: ReturnOptionMethod.EXCHANGE,
      creditType: StoreCreditType.GIFT_CARD,
      requireSendBack: false,
      chargeCustomerForNewOrderShipping: false,
      variantExchangeAddOn: false,
    },
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state) {
    return state;
  },
  toModel(state) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.SUBMIT_CLAIM,
      option: {
        method: getModelReturnOptionMethod(
          state.option.method,
          state.option.creditType,
        ),
        requireSendBack: state.option.requireSendBack,
        // Only meaningful for same-item exchange — drop the value when
        // the merchant switches the method so a stale toggle doesn't
        // silently apply if they switch back to refund/store-credit.
        chargeCustomerForNewOrderShipping:
          state.option.method === ReturnOptionMethod.EXCHANGE
            ? state.option.chargeCustomerForNewOrderShipping || false
            : false,
        carryOverLineItemProperties:
          state.option.method === ReturnOptionMethod.EXCHANGE
            ? (state.option.carryOverLineItemProperties ?? true)
            : undefined,
        variantExchangeAddOn: state.option.variantExchangeAddOn || false,
        variantExchangeAddOnVariantId:
          state.option.variantExchangeAddOnVariantId,
        variantExchangeAddOnTitle: state.option.variantExchangeAddOnTitle,
        variantExchangeAddOnAcceptButton:
          state.option.variantExchangeAddOnAcceptButton,
        variantExchangeAddOnRejectButton:
          state.option.variantExchangeAddOnRejectButton,
        carryOverLineItemPropertiesOnAddOnAccept:
          state.option.carryOverLineItemPropertiesOnAddOnAccept,
      },
    };
  },
  fromModel(model) {
    return submitClaimFromModel(model);
  },
});

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  type SubmitClaimOption,
  type SubmitClaimState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
